import {
  ComponentRef,
  Injectable,
  Injector,
  ViewContainerRef,
} from '@angular/core';
import { WidgetWrapperComponent } from '../components/widget-wrapper/widget-wrapper.component';
import { DynamicElementService } from '../services/dynamicElement.service';
import { WidgetType } from './WidgetType';

@Injectable()
export abstract class WidgetDock {
  protected refs = new Map<string, ComponentRef<WidgetWrapperComponent>>();

  constructor(
    protected widgetWrapperInsertionPoint: ViewContainerRef,
    protected dynamicElementService: DynamicElementService,
  ) {}

  /**
   * Creates an new WidgetwrapperComponent containing the widget wih name 'widgetname'
   */
  public activateWidget(widgetType: WidgetType): void {
    const widgetName = widgetType.name;

    const injector = Injector.create({
      providers: [
        {
          provide: 'widgetType',
          useValue: {
            widgetType: widgetType,
            widgetName: widgetName,
          },
        },
      ],
    });

    const componentRef = this.createComponentReference(widgetType, injector);

    // Save the componentRef of the wrapper to our map for easy access
    this.refs.set(widgetName, componentRef);
  }

  /**
   * Removes a WidgetWrapper by destroying the wrapper-component and the wrapped
   * widget-component.
   */
  public deactivateWidget(widgetType: WidgetType): void {
    const widgetName = widgetType.name;

    let wrapperComponentRef =
      this.dynamicElementService.getWrapperComponentRefForWidget(widgetName);

    if (wrapperComponentRef) {
      wrapperComponentRef.instance.onDockButtonClicked();
    }

    // From here on the wrapper should always be in the dock and can be removed from there
    wrapperComponentRef = this.refs.get(widgetName);

    this.removeFromDom(wrapperComponentRef);

    // Remove the wrapper-component-ref from our map
    this.refs.delete(widgetName);
  }

  public clearAll(widgetTypes: WidgetType[]): void {
    const dynamicRefs = widgetTypes
      .map((widgetType) => this.dynamicElementService.getWrapperComponentRefForWidget(widgetType.name))
      .filter(ref => !!ref);

    this.dynamicElementService.destroyAll(dynamicRefs);
  }

  public attachWidget(widgetName: string, wrapperCompRef: ComponentRef<WidgetWrapperComponent>, widgetOrder?: string[]) {
    this.refs.set(widgetName, wrapperCompRef);

    let index: number | undefined;

    if (widgetOrder) {
      index = [...this.refs.entries()]
        .filter(([, ref]) => ref)
        .map(([key]) => key)
        .sort((a, b) => widgetOrder.indexOf(a) - widgetOrder.indexOf(b))
        .indexOf(widgetName);
      if (index < 0) { index = undefined; }
    }

    // TODO: Insert at index
    this.widgetWrapperInsertionPoint.insert(wrapperCompRef.hostView, index);
  }

  public detachWidget(widgetName: string): ComponentRef<WidgetWrapperComponent> | null {
    const wrapperCompRef = this.refs.get(widgetName);
    if (!wrapperCompRef) {
      return null;
    }

    const viewIndex = this.widgetWrapperInsertionPoint.indexOf(wrapperCompRef.hostView);
    if (viewIndex >= 0) {
      this.widgetWrapperInsertionPoint.detach(viewIndex);
      this.refs.delete(widgetName);
    }

    return wrapperCompRef;
  }

  protected createComponentReference(widgetType: WidgetType, injector: Injector) {
    const componentRef = this.widgetWrapperInsertionPoint.createComponent(
      WidgetWrapperComponent,
      {  injector },
    );

    // Save the componentRef of the wrapper in the wrapper itself
    (<WidgetWrapperComponent>componentRef.instance).wrapperComponentRef =
      componentRef;

    return componentRef;
  }

  protected removeFromDom(wrapperComponentRef: ComponentRef<WidgetWrapperComponent>) {
    this.removeWrapperFromDOM(wrapperComponentRef);
  }

  /**
   * Removes the widgetWrapper containing the widget with name 'widgetName' from the DOM
   */
  protected removeWrapperFromDOMBy(widgetName: string) {
    const wrapperComponentRef = this.refs.get(widgetName);

    this.removeWrapperFromDOM(wrapperComponentRef);
  }

  /**
   * Removes the widgetWrapper containing the widget with name 'widgetName' from the DOM
   */
  protected removeWrapperFromDOM(wrapperComponentRef?: ComponentRef<WidgetWrapperComponent>) {
    if (wrapperComponentRef) {
      const index = this.widgetWrapperInsertionPoint.indexOf(wrapperComponentRef.hostView);

      if (index >= 0) {
        this.widgetWrapperInsertionPoint.remove(index);
      }
    }
  }
}
